/**
 * File helper utilities for PreviewFile component
 */

/**
 * Get file extension from file object
 */
export const getFileExtension = (f) => {
  if (f?.originalName) {
    const ext = f.originalName.split(".").pop()?.toLowerCase();
    if (ext && ext.length <= 5) return ext;
  }
  if (f?.name) {
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (ext && ext.length <= 5 && ext !== f.name.toLowerCase()) return ext;
  }
  if (f?.url) {
    try {
      const pathname = new URL(f.url).pathname;
      const ext = pathname.split(".").pop()?.toLowerCase();
      if (ext && ext.length <= 5) return ext;
    } catch {
      const cleanUrl = f.url.split("?")[0];
      const ext = cleanUrl.split(".").pop()?.toLowerCase();
      if (ext && ext.length <= 5) return ext;
    }
  }
  if (f?.mimeType) {
    const mime = String(f.mimeType).toLowerCase();
    if (mime.includes("pdf")) return "pdf";
    if (mime.startsWith("image/")) return mime.split("/")[1];
    if (mime.startsWith("video/")) return mime.split("/")[1];
    if (mime.startsWith("audio/")) return mime.split("/")[1];
    if (mime.includes("json")) return "json";
    if (mime.includes("xml")) return "xml";
    if (mime.includes("csv")) return "csv";
    if (mime.includes("plain")) return "txt";
  }
  return null;
};

/**
 * Check if file is an image
 */
export const isImageFile = (f) => {
  if (String(f?.mimeType || "").toLowerCase().startsWith("image/")) return true;
  const ext = getFileExtension(f);
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext);
};

/**
 * Check if file is a PDF
 */
export const isPDFFile = (f) => {
  if (String(f?.mimeType || "").toLowerCase().includes("pdf")) return true;
  const ext = getFileExtension(f);
  return ext === "pdf";
};

/**
 * Check if file is a text file
 */
export const isTextFile = (f) => {
  const mime = String(f?.mimeType || "").toLowerCase();
  if (
    mime.startsWith("text/") ||
    [
      "application/json",
      "application/xml",
      "application/javascript",
      "application/x-javascript",
      "application/yaml",
      "application/x-yaml",
    ].includes(mime)
  ) {
    return true;
  }
  const ext = getFileExtension(f);
  return [
    "txt",
    "json",
    "csv",
    "xml",
    "md",
    "log",
    "yaml",
    "yml",
    "html",
    "css",
    "js",
    "jsx",
    "ts",
    "tsx",
    "py",
    "java",
    "cpp",
    "c",
    "h",
  ].includes(ext);
};

/**
 * Check if file is a video
 */
export const isVideoFile = (f) => {
  const ext = getFileExtension(f);
  if (["mp4", "webm", "ogg", "mov", "avi", "m4v", "3gp", "mkv"].includes(ext)) return true;
  if (f?.mimeType?.startsWith("video/")) return true;
  return false;
};

/**
 * Check if file is an audio file
 */
export const isAudioFile = (f) => {
  if (String(f?.mimeType || "").toLowerCase().startsWith("audio/")) return true;
  const ext = getFileExtension(f);
  return ["mp3", "wav", "ogg", "m4a", "aac"].includes(ext);
};

/**
 * Check if file is an Office document
 */
export const isOfficeFile = (f) => {
  const mime = String(f?.mimeType || "").toLowerCase();
  if (
    mime.includes("officedocument") ||
    mime.includes("msword") ||
    mime.includes("excel") ||
    mime.includes("powerpoint")
  ) {
    return true;
  }
  const ext = getFileExtension(f);
  return ["xlsx", "xls", "docx", "doc", "pptx", "ppt"].includes(ext);
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

/**
 * Check if file is a PDF by mimeType or extension
 */
export const isPDF = (file) => {
  return (
    file?.mimeType?.includes("pdf") ||
    file?.originalName?.toLowerCase().endsWith(".pdf")
  );
};
